// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { getChats, getChatMessages, sendChatMessage, markChatMessagesRead, createChat, getAllBookings } from './services/apiService';
import authService from './authService';

// Map the logged-in employee role to the chat category/role stored in the DB.
// The DB uses FINANCIAL where authService uses ACCOUNTING.
const mapRoleToCategory = (role) => {
  if (role === 'ACCOUNTING') return 'FINANCIAL';
  return role || '';
};

// Map a chat category to a valid recipient_role for staff -> customer replies.
// recipient_role constraint allows ADMIN (not ADMINISTRATOR); category uses ADMINISTRATOR.
const categoryToRecipientRole = (cat) => (cat === 'ADMINISTRATOR' ? 'ADMIN' : (cat || 'ADMIN'));

// Booking field accessors (top-level or nested booking_data) for the New-chat search.
const newCustField = (b, k) => {
  if (!b) return '';
  if (b[k] !== undefined && b[k] !== null && b[k] !== '') return b[k];
  if (b.booking_data && b.booking_data[k] != null) return b.booking_data[k];
  return '';
};
const newCustNum = (b) => String(newCustField(b, 'bookingNumber') || (b && b.booking_number) || (b && b.bookingCode) || (b && b.charterCode) || (b && b.code) || '');
const newStripNum = (x) => String(x || '').replace(/^[^0-9]*/, '');

// Convert a DB chat row into the shape the UI expects.
const mapDbChat = (c) => ({
  id: c.chat_id,
  bookingCode: c.booking_code,
  customerName: c.customer_name || 'Customer',
  vesselName: c.vessel_name || '',
  category: c.category,
  status: c.status,
  isVisitor: c.is_visitor,
  createdAt: c.created_at,
  lastMessage: c.last_message || '',
  lastMessageTime: c.last_message_time || c.last_message_at,
  unreadCount: Number(c.unread_count) || 0,
  messages: [],
});

// Convert a DB message row into the shape the UI expects.
const mapDbMessage = (m) => ({
  id: String(m.id),
  sender: m.sender_role,
  senderName: m.sender_name,
  message: m.message,
  timestamp: m.timestamp,
  read: Number(m.read_status) === 1,
});

// WhatsApp-look helpers for the chat list (avatar color/letter + relative time)
const AVATAR_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const avatarColor = (name) => { let h = 0; const s = String(name || '?'); for (let i = 0; i < s.length; i++) { h = s.charCodeAt(i) + ((h << 5) - h); } return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; };
const avatarLetter = (name) => String(name || '?').trim().charAt(0).toUpperCase() || '?';
const formatChatTime = (ts) => { if (!ts) return ''; const d = new Date(String(ts).replace(' ', 'T').replace(/Z?$/, 'Z')); if (isNaN(d.getTime())) return ''; const now = new Date(); const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' }); if (fmt.format(d) === fmt.format(now)) { return d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Athens' }); } return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Athens' }); };
const formatMsgDateTime = (ts) => { if (!ts) return ''; const d = new Date(String(ts).replace(' ', 'T').replace(/Z?$/, 'Z')); if (isNaN(d.getTime())) return ''; const now = new Date(); const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' }); const hm = d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Athens' }); if (fmt.format(d) === fmt.format(now)) return hm; const dm = d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Athens' }); return dm + ' ' + hm; };
const CHAT_SETTINGS_URL = 'https://yachtmanagementsuite.com/api/chat-settings.php';

export default function ChatManagementModal({ onClose }: { onClose: () => void }) {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ technical: 0, financial: 0, booking: 0 });
  const [showNew, setShowNew] = useState(false);
  const [newMode, setNewMode] = useState('staff'); // 'staff' | 'cust'
  const [staffList, setStaffList] = useState([]);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custBookings, setCustBookings] = useState([]);
  const messagesEndRef = useRef(null);
  const [soundList, setSoundList] = useState([]);
  const [mySound, setMySound] = useState('');
  const [myMuted, setMyMuted] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const mySoundRef = useRef('');
  const myMutedRef = useRef(false);
  const prevTotalRef = useRef(-1);

  useEffect(() => {
    loadChats();
    const interval = setInterval(loadChats, 3000);
    return () => clearInterval(interval);
  }, [filterCategory]);

  // Request notification permission
  useEffect(() => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Per-staff sound settings + new-message beep
  const playNotify = () => {
    try {
      if (myMutedRef.current) return;
      const file = mySoundRef.current || 'liecio-message-notification-190034.mp3';
      const a = new Audio('/sounds/' + file);
      a.play().catch(() => {});
    } catch (e) {}
  };

  useEffect(() => {
    const userKey = authService.getCurrentUser()?.code || '';
    let cancelled = false;
    (async () => {
      try {
        const sres = await fetch(CHAT_SETTINGS_URL + '?action=sounds');
        const sj = await sres.json();
        if (!cancelled && sj && sj.success) setSoundList(sj.sounds || []);
      } catch (e) {}
      if (!userKey) return;
      try {
        const gres = await fetch(CHAT_SETTINGS_URL + '?action=get&user_key=' + encodeURIComponent(userKey));
        const gj = await gres.json();
        if (!cancelled && gj && gj.success) {
          setMySound(gj.notification_sound || '');
          setMyMuted(Number(gj.muted) === 1);
        }
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { mySoundRef.current = mySound; myMutedRef.current = myMuted; }, [mySound, myMuted]);

  const saveSoundSettings = async () => {
    const userKey = authService.getCurrentUser()?.code || '';
    if (!userKey) { setShowSoundSettings(false); return; }
    try {
      await fetch(CHAT_SETTINGS_URL + '?action=set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_key: userKey, notification_sound: mySound, muted: myMuted ? 1 : 0 })
      });
    } catch (e) {
      console.error('Error saving sound settings:', e);
    }
    setShowSoundSettings(false);
  };

  const previewSound = (name) => {
    try { const a = new Audio('/sounds/' + name); a.play().catch(() => {}); } catch (e) {}
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedChat?.messages]);

  const loadChats = async () => {
    try {
      const user = authService.getCurrentUser();
      const role = user?.role || '';
      const isAdmin = role === 'ADMIN';

      // ADMIN sees every chat; everyone else only their own category.
      const result = isAdmin
        ? await getChats()
        : await getChats({ category: mapRoleToCategory(role) });

      if (!result || !result.success) { setLoading(false); return; }

      const allChats = (result.chats || []).map(mapDbChat);

      setCounts({
        technical: allChats.filter(c => c.category === 'TECHNICAL').length,
        financial: allChats.filter(c => c.category === 'FINANCIAL').length,
        booking: allChats.filter(c => c.category === 'BOOKING').length,
      });

      // ADMIN can narrow the list with the filter buttons; others are already scoped.
      const visible = (isAdmin && filterCategory !== 'ALL')
        ? allChats.filter(c => c.category === filterCategory)
        : allChats;

      visible.sort((a, b) => new Date(b.lastMessageTime || b.createdAt).getTime() - new Date(a.lastMessageTime || a.createdAt).getTime());
      setChats(visible);

      // Beep when total unread increases (not on the first load).
      const newTotal = visible.reduce((sum, c) => sum + (Number(c.unreadCount) || 0), 0);
      if (prevTotalRef.current !== -1 && newTotal > prevTotalRef.current) { playNotify(); }
      prevTotalRef.current = newTotal;

      // Keep the open conversation's messages fresh.
      if (selectedChat) {
        const msgRes = await getChatMessages(selectedChat.id);
        if (msgRes && msgRes.success) {
          setSelectedChat(prev => prev ? { ...prev, messages: (msgRes.messages || []).map(mapDbMessage) } : prev);
        }
      }
    } catch (e) {
      console.error('Error loading chats:', e);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendReply = async () => {
    if (!selectedChat || !replyMessage.trim()) return;

    const currentUser = authService.getCurrentUser();
    const senderName = currentUser?.name || 'Administrator';
    const senderId = currentUser?.code || '';
    const senderRole = mapRoleToCategory(currentUser?.role || 'ADMIN');
    const text = replyMessage.trim();

    setReplyMessage('');

    try {
      await sendChatMessage({
        chat_id: selectedChat.id,
        booking_code: selectedChat.bookingCode || '',
        vessel_name: selectedChat.vesselName || '',
        customer_name: selectedChat.customerName || '',
        sender_id: senderId,
        sender_name: senderName,
        sender_role: senderRole,
        recipient_role: categoryToRecipientRole(selectedChat.category),
        category: selectedChat.category,
        message: text,
      });

      // Refresh the open conversation's messages from the DB.
      const msgRes = await getChatMessages(selectedChat.id);
      if (msgRes && msgRes.success) {
        setSelectedChat(prev => prev ? { ...prev, messages: (msgRes.messages || []).map(mapDbMessage) } : prev);
      }

      loadChats();
      setTimeout(scrollToBottom, 100);
    } catch (e) {
      console.error('Error sending reply:', e);
    }
  };

  // ===== New conversation: staff list + customer search =====
  useEffect(() => {
    if (!showNew || newMode !== 'staff') return;
    let cancelled = false;
    (async () => {
      try {
        const me = authService.getCurrentUser()?.code || '';
        const all = await authService.fetchEmployeesFromAPI();
        if (cancelled) return;
        setStaffList((all || []).filter((e) => e.enabled !== false && e.code !== me && e.code !== 'CHECKIN2025'));
      } catch (err) {
        console.error('Error fetching staff list:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [showNew, newMode]);

  useEffect(() => {
    if (!showNew || custBookings.length) return;
    let cancelled = false;
    (async () => {
      try {
        const bs = await getAllBookings();
        if (!cancelled) setCustBookings(bs || []);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [showNew]);

  useEffect(() => {
    if (!showNew || newMode !== 'cust') return;
    const q = custSearch.trim().toLowerCase();
    if (!q) { setCustResults([]); return; }
    const qDigits = q.replace(/[^0-9]/g, '');
    const out = (custBookings || []).filter((b) => {
      const num = newStripNum(newCustNum(b)).toLowerCase();
      const ch = (newCustField(b, 'chartererFirstName') + ' ' + newCustField(b, 'chartererLastName')).trim().toLowerCase();
      const sk = (newCustField(b, 'skipperFirstName') + ' ' + newCustField(b, 'skipperLastName')).trim().toLowerCase();
      const ves = String(newCustField(b, 'vesselName') || '').toLowerCase();
      return (qDigits && num.indexOf(qDigits) !== -1)
        || (ch && ch.indexOf(q) !== -1)
        || (sk && sk.indexOf(q) !== -1)
        || (ves && ves.indexOf(q) !== -1);
    }).slice(0, 15);
    setCustResults(out);
  }, [custSearch, showNew, newMode, custBookings]);

  const openStaffChat = async (emp) => {
    const meCode = authService.getCurrentUser()?.code || '';
    const ids = [meCode, emp.code].sort();
    const chatId = 'staff-' + ids[0] + '-' + ids[1];
    try {
      await createChat({ chat_id: chatId, booking_code: chatId, category: 'ADMINISTRATOR', is_visitor: 0, customer_name: emp.name });
      const msgRes = await getChatMessages(chatId);
      setSelectedChat({
        id: chatId,
        customerName: emp.name,
        category: 'ADMINISTRATOR',
        bookingCode: chatId,
        vesselName: '',
        messages: (msgRes && msgRes.success) ? (msgRes.messages || []).map(mapDbMessage) : []
      });
      await markChatMessagesRead(chatId, meCode);
      setShowNew(false);
      loadChats();
    } catch (e) {
      console.error('Error starting staff chat:', e);
    }
  };

  const openCustomerChat = async (booking, peerName) => {
    const meCode = authService.getCurrentUser()?.code || '';
    const num = newStripNum(newCustNum(booking));
    const vessel = String(newCustField(booking, 'vesselName') || '');
    try {
      const res = await createChat({ booking_code: num, category: 'BOOKING', vessel_name: vessel, customer_name: peerName, is_visitor: 0 });
      const chatId = (res && res.chat && res.chat.chat_id) ? res.chat.chat_id : (num + '-BOOKING');
      const msgRes = await getChatMessages(chatId);
      setSelectedChat({
        id: chatId,
        customerName: peerName,
        category: 'BOOKING',
        bookingCode: num,
        vesselName: vessel,
        messages: (msgRes && msgRes.success) ? (msgRes.messages || []).map(mapDbMessage) : []
      });
      await markChatMessagesRead(chatId, meCode);
      setShowNew(false);
      loadChats();
    } catch (e) {
      console.error('Error starting customer chat:', e);
    }
  };

  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    try {
      const msgRes = await getChatMessages(chat.id);
      if (msgRes && msgRes.success) {
        setSelectedChat({ ...chat, messages: (msgRes.messages || []).map(mapDbMessage) });
      }
      await markChatMessagesRead(chat.id, authService.getCurrentUser()?.code || '');
    } catch (e) {
      console.error('Error selecting chat:', e);
    }
    loadChats();
  };

  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'TECHNICAL': return '🔧';
      case 'FINANCIAL': return '💰';
      case 'BOOKING': return '📅';
      default: return '💬';
    }
  };

  const getCategoryColor = (category: string) => {
    switch(category) {
      case 'TECHNICAL': return 'from-blue-500 to-cyan-500';
      case 'FINANCIAL': return 'from-green-500 to-emerald-500';
      case 'BOOKING': return 'from-purple-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getCategoryName = (category: string) => {
    switch(category) {
      case 'TECHNICAL': return 'Technical';
      case 'FINANCIAL': return 'Financial';
      case 'BOOKING': return 'Booking';
      default: return 'Unknown';
    }
  };

  const technicalCount = counts.technical;
  const financialCount = counts.financial;
  const bookingCount = counts.booking;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Chat Management</h2>
            <p className="text-sm text-blue-100">Διαχείριση όλων των συνομιλιών</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setShowSoundSettings(true)} className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors" title="Ήχος ειδοποίησης" type="button">
              <span className="text-2xl">🔔</span>
            </button>
            <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors">
              <span className="text-2xl">✕</span>
            </button>
          </div>
        </div>

        {showSoundSettings && (
          <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-800">Ήχος ειδοποίησης</h3>
              <button onClick={() => setShowSoundSettings(false)} className="text-gray-500 hover:bg-gray-100 rounded-lg p-1" type="button"><span className="text-xl">✕</span></button>
            </div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={myMuted} onChange={(e) => setMyMuted(e.target.checked)} />
              <span className="text-sm font-semibold text-gray-700">Σίγαση</span>
            </label>
            <div className={`space-y-1 mb-3 max-h-48 overflow-y-auto ${myMuted ? 'opacity-40 pointer-events-none' : ''}`}>
              {soundList.length === 0 ? (
                <div className="text-center text-gray-500 p-4 text-sm">Καμία διαθέσιμη επιλογή</div>
              ) : (
                soundList.map((name) => (
                  <div key={name} className={`flex items-center gap-2 p-2 rounded-lg ${mySound === name ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                    <input type="radio" name="modalNotifSound" checked={mySound === name} onChange={() => setMySound(name)} />
                    <span className="flex-1 text-sm text-gray-700 truncate">{name}</span>
                    <button onClick={() => previewSound(name)} className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-1" type="button" title="Προεπισκόπηση"><span className="text-lg">▶</span></button>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={saveSoundSettings} className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold text-sm" type="button">Αποθήκευση</button>
              <button onClick={() => setShowSoundSettings(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold text-sm" type="button">Κλείσιμο</button>
            </div>
          </div>
        )}

        {/* Category Filters */}
        <div className="p-4 bg-gray-50 border-b flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterCategory('ALL')}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${filterCategory === 'ALL' ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            📋 Όλα ({technicalCount + financialCount + bookingCount})
          </button>
          <button
            onClick={() => setFilterCategory('TECHNICAL')}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${filterCategory === 'TECHNICAL' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            🔧 Technical ({technicalCount})
          </button>
          <button
            onClick={() => setFilterCategory('FINANCIAL')}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${filterCategory === 'FINANCIAL' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            💰 Financial ({financialCount})
          </button>
          <button
            onClick={() => setFilterCategory('BOOKING')}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${filterCategory === 'BOOKING' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            📅 Booking ({bookingCount})
          </button>
          <button
            onClick={() => { setShowNew(true); setNewMode('staff'); }}
            className="px-4 py-2 rounded-xl font-semibold transition-all bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
          >
            ➕ Νέα
          </button>
        </div>

        {showNew && (
          <div className="p-4 border-b bg-white max-h-[50vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800">Νέα συνομιλία</h3>
              <button onClick={() => setShowNew(false)} className="text-gray-500 hover:bg-gray-100 rounded-lg p-1" type="button">
                <span className="text-xl">✕</span>
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setNewMode('staff')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${newMode === 'staff' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`} type="button">Προσωπικό</button>
              <button onClick={() => setNewMode('cust')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${newMode === 'cust' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`} type="button">Πελάτες</button>
            </div>

            {newMode === 'staff' && (
              <div className="space-y-2">
                {staffList.length === 0 ? (
                  <div className="text-center text-gray-500 p-4 text-sm">Φόρτωση...</div>
                ) : (
                  staffList.map((emp) => (
                    <button key={emp.code} onClick={() => openStaffChat(emp)} className="w-full p-3 rounded-xl shadow-sm bg-white hover:bg-gray-50 flex items-center justify-between transition-colors text-left" type="button">
                      <span className="font-semibold text-gray-800">{emp.name}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">{emp.role}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {newMode === 'cust' && (
              <div className="space-y-2">
                <input type="text" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Αναζήτηση (αριθμός, όνομα, σκάφος)" className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white" style={{ color: '#1f2937', backgroundColor: '#ffffff' }} autoComplete="off" />
                {custSearch.trim() === '' ? (
                  <div className="text-center text-gray-400 p-4 text-sm">Πληκτρολόγησε για αναζήτηση</div>
                ) : custResults.length === 0 ? (
                  <div className="text-center text-gray-400 p-4 text-sm">Κανένα αποτέλεσμα</div>
                ) : (
                  custResults.map((b, bi) => {
                    const num = newStripNum(newCustNum(b));
                    const ves = String(newCustField(b, 'vesselName') || '');
                    const chName = (newCustField(b, 'chartererFirstName') + ' ' + newCustField(b, 'chartererLastName')).trim();
                    const skName = (newCustField(b, 'skipperFirstName') + ' ' + newCustField(b, 'skipperLastName')).trim();
                    const contacts = [];
                    if (chName && skName && chName.toLowerCase() !== skName.toLowerCase()) {
                      contacts.push({ name: chName, role: 'Ναυλωτής' });
                      contacts.push({ name: skName, role: 'Σκίπερ' });
                    } else if (chName) {
                      contacts.push({ name: chName, role: 'Ναυλωτής' });
                    } else if (skName) {
                      contacts.push({ name: skName, role: 'Σκίπερ' });
                    } else {
                      contacts.push({ name: 'Booking ' + num, role: '' });
                    }
                    return (
                      <div key={(newCustNum(b) || '') + '-' + bi} className="bg-white rounded-xl shadow-sm p-2">
                        <div className="text-xs text-gray-500 mb-1">#{num} • {ves}</div>
                        {contacts.map((ct, ci) => (
                          <button key={ci} onClick={() => openCustomerChat(b, ct.name)} className="w-full p-2 rounded-lg hover:bg-gray-50 flex items-center justify-between text-left" type="button">
                            <span className="font-semibold text-gray-800 text-sm">{ct.name}</span>
                            {ct.role && <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{ct.role}</span>}
                          </button>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Chats List */}
          <div className="w-1/3 border-r overflow-y-auto bg-gray-50">
            {loading && chats.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">⏳</div>
                <p>Φόρτωση...</p>
              </div>
            ) : chats.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">📭</div>
                <p>Δεν υπάρχουν συνομιλίες</p>
              </div>
            ) : (
              chats.map(chat => {
                const unreadCount = chat.unreadCount || 0;
                const isStaffChat = String(chat.id || '').indexOf('staff-') === 0;
                const lastMsg = String(chat.lastMessage || '');
                const preview = lastMsg.length > 35 ? lastMsg.substring(0, 35) + '...' : lastMsg;
                return (
                  <button
                    key={chat.id}
                    onClick={() => handleSelectChat(chat)}
                    className={`w-full px-4 py-3 flex items-center gap-3 border-b border-gray-100 text-left transition-colors ${selectedChat?.id === chat.id ? 'bg-white border-l-4 border-blue-500' : 'hover:bg-gray-50'}`}
                  >
                    <span
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: avatarColor(chat.customerName) }}
                    >
                      {avatarLetter(chat.customerName)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1 min-w-0">
                          <span className="font-bold text-gray-800 truncate">{chat.customerName}</span>
                          {isStaffChat && <span className="text-xs px-1 rounded bg-emerald-100 text-emerald-700 flex-shrink-0">Staff</span>}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatChatTime(chat.lastMessageTime)}</span>
                      </span>
                      <span className="flex items-center justify-between gap-2 mt-0.5">
                        <span className={`text-xs truncate ${unreadCount > 0 ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>{preview || '\u00a0'}</span>
                        {unreadCount > 0 && (
                          <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 animate-pulse">{unreadCount}</span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Chat View */}
          <div className="flex-1 flex flex-col">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className={`bg-gradient-to-r ${getCategoryColor(selectedChat.category)} text-white p-4`}>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getCategoryIcon(selectedChat.category)}</span>
                    <div>
                      <div className="font-bold">{selectedChat.customerName}</div>
                      <div className="text-sm text-white/80">{selectedChat.vesselName} • {getCategoryName(selectedChat.category)}</div>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-br from-slate-50 to-blue-50">
                  {selectedChat.messages.length === 0 ? (
                    <div className="text-center p-8 text-gray-500">
                      <div className="text-4xl mb-2">💬</div>
                      <p>Δεν υπάρχουν μηνύματα ακόμα</p>
                    </div>
                  ) : (
                    selectedChat.messages.map(msg => {
                      const isCustomer = msg.sender === 'CUSTOMER';
                      return (
                        <div key={msg.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${isCustomer ? 'bg-white shadow-md text-gray-800' : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'}`}>
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-semibold text-xs">{msg.senderName}</span>
                              <span className={`text-xs ${isCustomer ? 'text-gray-500' : 'text-blue-100'}`}>
                                {formatMsgDateTime(msg.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                <div className="p-4 bg-white border-t">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                      placeholder="Γράψε την απάντησή σου..."
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                      style={{
                        color: '#1f2937',
                        backgroundColor: '#ffffff'
                      }}
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim()}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Αποστολή 📤
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-6xl mb-4">💬</div>
                  <p>Επιλέξτε μια συνομιλία για να την δείτε</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Placeholder styling */}
      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
